import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import type { FrequencyPeriod, RecoveryConfig } from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CONFIG_CARD_GAP } from 'scenes/RecoveryAI/components/ConfigCard'
import SettingsPaneChrome from 'scenes/RecoveryAI/components/SettingsPaneChrome'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import EscalationRulesBody, {
  EscalationRulesChange,
} from '../Wizard/steps/limits/EscalationRulesBody'
import {
  coerceFrequentPeriod,
  daysToPeriod,
  DEFAULT_FREQUENT_PERIOD,
  periodToDays,
} from '../Wizard/steps/limits/periodFormat'
import {
  clampNonNegativeThreshold,
  DEFAULT_AUTO_RESOLUTION_THRESHOLD,
  DEFAULT_ESCALATION_THRESHOLD,
} from '../Wizard/useWizardSubmit'
import { useSaveAndTest } from './useSaveAndTest'
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt'

const t = buildTranslate('recovery_ai.settings')
// AI-903: title/subtitle reuse the same copy the wizard limits step's
// EscalationRulesCard shows in its ConfigCard header, so dropping the box here
// doesn't change the heading text — only where it's rendered (SettingsPaneChrome
// instead of a second boxed card). Mirrors RecoveryGenerosityPane's two-scope
// translate split (wizard copy + settings-only copy).
const tRules = buildTranslate('recovery_ai.wizard.limits')

// Horizontal gap between the two footer buttons (extracted per GAP-1 so the
// layout magic number isn't an inline literal in css({})).
const SAVE_FOOTER_GAP = '16px'

// AI-302 (FR-41): display-format default for the gaming period, used by the
// hydration fallback + the isDirty config-side default (the DB equivalent is
// periodToDays(DEFAULT_DISPLAY_PERIOD)). Mirrors the '30d' the frequent period
// initializes to.
const DEFAULT_DISPLAY_PERIOD: FrequencyPeriod = '30d'

// Local controlled mirror of the escalation fields the card edits. Held as
// strings (matching the card's FormControl inputs + the wizard's
// useWizardHydration string-conversion); converted to numbers only at persist
// time via clampNonNegativeThreshold. The gaming period is the '30d' DISPLAY
// format here; the pane maps it to/from the integer day-count at the
// payload/hydration boundary (AI-327).
type EscalationState = {
  frequentThreshold: string
  frequencyPeriod: FrequencyPeriod
  repeatedThreshold: string
  autoResolutionThreshold: string
  autoResolutionPeriod: FrequencyPeriod
}

// Post-activation Settings pane for the Frequent + Consecutive escalation
// rules. Reuses the (now controlled) wizard EscalationRulesBody directly under
// SettingsPaneChrome (AI-903: EscalationRulesCard's ConfigCard box is only for
// the wizard step / Agent-Tuning modal) — data-fetching + persistence live
// here, the body stays presentational. Gated in-pane on
// onboarding_completed (D15) — the editable card only renders once RecoveryAI
// is activated. AI-121: edits accumulate in local state and persist on the
// explicit Save / Save-and-test buttons (no more debounced auto-save).
const EscalationRulesPane = () => {
  const [css] = useStyletron()
  // AI-64: admin-only writes. View-only operators see the rules read-only (the
  // card renders disabled and the Save footer is withheld). Mirrors the CASL
  // gate ActiveLocationsPane / EscalationContacts already apply.
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  const {
    data: config,
    isLoading,
    isError: isFetchError,
  } = useGetRecoveryConfigQuery()

  const [state, setState] = useState<EscalationState>({
    frequentThreshold: String(DEFAULT_ESCALATION_THRESHOLD),
    frequencyPeriod: DEFAULT_FREQUENT_PERIOD,
    repeatedThreshold: String(DEFAULT_ESCALATION_THRESHOLD),
    autoResolutionThreshold: String(DEFAULT_AUTO_RESOLUTION_THRESHOLD),
    autoResolutionPeriod: DEFAULT_DISPLAY_PERIOD,
  })

  // recovery-ai-gaming-review gates the "Suspected abuse" section here (mirrors
  // the Settings tab). Off (default) → the section is hidden and its fields never
  // ride in a save, so a Frequent/Consecutive edit can't touch the stored value.
  const suspectedAbuseEnabled = useFlag('recovery-ai-gaming-review', false)

  // AI-302 (Darshan review): only round-trip the gaming fields when the config
  // GET actually advertises them — i.e. the companion nexus change (column +
  // Grape permit + entity expose) has shipped, so the field comes back on GET.
  // Until then we OMIT them from the PUT rather than sending FE defaults: Grape
  // drops undeclared params today (inert), but once the column lands an
  // unconditional send would let an UNRELATED escalation edit silently overwrite
  // a merchant's stored gaming value with the FE default. The companion exposes
  // both auto_resolution_* columns atomically, so the threshold's presence is a
  // sufficient sentinel for the pair. Self-heals the moment nexus exposes it.
  const gamingConfigLive = config?.auto_resolution_threshold !== undefined

  // Single sentinel for "the gaming section is live" — mirrors the modal's
  // EscalationRulesModalBody `showGaming` so the two surfaces agree on when the
  // section renders, persists, and dirties. Do not gate any of the three on
  // `suspectedAbuseEnabled` alone (see AI-329 review): the flag can be on before
  // the companion nexus change ships, and rendering the control without it
  // going live in the payload leaves an editable-but-inert input.
  const showGaming = suspectedAbuseEnabled && gamingConfigLive

  // Same sentinel logic for the frequent-complaint period. The nexus entity does
  // not expose frequent_complaint_period yet (unwired, AI-438 — the column is a
  // legacy '{n}_days' VARCHAR), so it arrives undefined and must NOT ride the
  // PUT: an unconditional send would let an unrelated threshold edit overwrite
  // the merchant's stored window with the FE default the moment AI-438 permits
  // the param. Self-heals when the entity starts advertising it. Unlike the
  // gaming section the frequent rule is always visible, so the <select> still
  // renders (showing the enforced 30d) — only its persistence is gated.
  const frequentPeriodLive = config?.frequent_complaint_period !== undefined

  // Derive the payload from local state, clamping the string thresholds to safe
  // non-negative numbers with the same fallback the wizard's
  // mapStepToConfig('limits') uses, so a Settings save matches the wizard. The
  // frequent/repeated fields always ride in one PUT; the gaming fields ride
  // along ONLY when showGaming (see sentinel above). The gaming threshold
  // clamps like the others; the period maps display → integer day-count ('30d'
  // → 30). Reconciliation #4/#6/#7.
  const buildPayload = useCallback((): Partial<RecoveryConfig> => {
    const payload: Partial<RecoveryConfig> = {
      frequent_complaint_threshold: clampNonNegativeThreshold(
        state.frequentThreshold,
        DEFAULT_ESCALATION_THRESHOLD
      ),
      repeated_complaint_threshold: clampNonNegativeThreshold(
        state.repeatedThreshold,
        DEFAULT_ESCALATION_THRESHOLD
      ),
    }
    // Gate the period like the gaming fields: only send it once the config
    // exposes it (frequentPeriodLive). Until then it's display-only and a
    // threshold save leaves the stored window untouched.
    if (frequentPeriodLive) {
      payload.frequent_complaint_period = state.frequencyPeriod
    }
    if (showGaming) {
      payload.auto_resolution_threshold = clampNonNegativeThreshold(
        state.autoResolutionThreshold,
        DEFAULT_AUTO_RESOLUTION_THRESHOLD
      )
      payload.auto_resolution_period_days = periodToDays(
        state.autoResolutionPeriod
      )
    }
    return payload
  }, [state, showGaming, frequentPeriodLive])

  // AI-121 (R4): the form is dirty when the payload it would persist differs
  // from the saved config. Compare the same three fields the PUT sends, against
  // the (clamped) values currently in local state — clamping both sides means a
  // pristine "2" vs an out-of-range "-7" edit are judged the same way the save
  // would treat them. Pristine forms are non-dirty, so Save can't fire a no-op
  // PUT (and a false "Saved" banner) on an untouched form.
  const isDirty = useMemo(() => {
    if (!config) return false
    const payload = buildPayload()
    const baseDirty =
      payload.frequent_complaint_threshold !==
        config.frequent_complaint_threshold ||
      payload.repeated_complaint_threshold !==
        config.repeated_complaint_threshold ||
      // The period only participates in dirtiness when the config exposes it
      // (buildPayload omits it otherwise — same gate as the gaming fields). When
      // live, compare the coerced values so a recognized token round-trips
      // unchanged and an out-of-vocabulary value (e.g. legacy '30_days') resolves
      // to the display default on both sides — a freshly-hydrated form reads
      // PRISTINE, not dirty-on-load.
      (frequentPeriodLive &&
        payload.frequent_complaint_period !==
          coerceFrequentPeriod(config.frequent_complaint_period))
    // The gaming fields only participate in dirtiness when showGaming
    // (buildPayload omits them otherwise). Skipping the comparison otherwise
    // keeps a freshly-hydrated form PRISTINE on load — comparing an omitted
    // payload key against the default would read dirty-on-load and wrongly
    // enable Save. When the section isn't live, it's also hidden, so a
    // Frequent/Consecutive save never sends or dirties the stored gaming value.
    if (!showGaming) return baseDirty
    // When live, compare against the SAME defaults hydration uses so a recognized
    // value round-trips unchanged and an out-of-enum day-count (Reconciliation
    // #7, e.g. 45) coalesces to the display default's day-count on both sides — a
    // freshly-hydrated form reads PRISTINE rather than dirty-on-load (which would
    // let Save silently rewrite the unknown value to the default).
    const configAutoThreshold =
      config.auto_resolution_threshold ?? DEFAULT_AUTO_RESOLUTION_THRESHOLD
    const configAutoPeriodDays = periodToDays(
      daysToPeriod(config.auto_resolution_period_days) ?? DEFAULT_DISPLAY_PERIOD
    )
    return (
      baseDirty ||
      payload.auto_resolution_threshold !== configAutoThreshold ||
      payload.auto_resolution_period_days !== configAutoPeriodDays
    )
  }, [config, buildPayload, showGaming, frequentPeriodLive])

  // Save orchestration (mutation, success flag, error/loading flags,
  // dirty-gating + canSave, unsaved-guard bypass) shared with the other
  // RecoveryAI Settings panes via this hook. AI-1114: escalation rules aren't
  // testable in the simulator sense the other panes are, so this pane never
  // renders Save-and-test — handleSaveAndTest is intentionally unused here.
  const {
    handleSave,
    clearSuccess,
    saveSuccess,
    isSaving,
    isSaveError,
    canSave,
    guardBypassRef,
  } = useSaveAndTest(buildPayload, isDirty)

  // R3: warn before leaving the route (in-app nav + refresh/close) while dirty.
  // `guardBypassRef` only ever flips true inside handleSaveAndTest (AI-174) —
  // this pane destructures `handleSave`, not `handleSaveAndTest` (AI-1114: no
  // Save-and-test here), so the ref stays permanently false and this call is
  // effectively `useUnsavedChangesPrompt(isDirty)`. Passed anyway (the param
  // is optional on useUnsavedChangesPrompt) so this call keeps the same shape
  // as the other Settings panes rather than special-casing this one.
  const unsavedChangesPrompt = useUnsavedChangesPrompt(isDirty, guardBypassRef)

  // "Latest value" mirror of the mutation's in-flight flag so the hydration
  // effect (deps: [config]) can read it without re-running when it flips —
  // re-running on the false→true→false save cycle would rehydrate from the
  // STALE cached config before the post-save invalidation refetch lands.
  const isSavingRef = useRef(false)
  isSavingRef.current = isSaving

  // Hydrate local state from the server config whenever a fresh config arrives
  // AND no save is in flight. A once-only gate would also block every
  // legitimate refetch — e.g. a wizard mutation elsewhere in the SPA
  // invalidates the RecoveryConfig tag, or an admin saves from another tab —
  // leaving this pane showing (and later re-saving) stale thresholds. So the
  // guard is scoped to in-flight saves only: rehydrating during a PUT would
  // clobber what the merchant just submitted; once the pane is quiescent,
  // server truth wins. String(...) mirrors useWizardHydration's conversion for
  // the card's controlled inputs.
  useEffect(() => {
    if (!config) return
    if (isSavingRef.current) return
    const next: EscalationState = {
      frequentThreshold: String(config.frequent_complaint_threshold),
      // frequent_complaint_period is not exposed by the nexus entity (unwired,
      // AI-438), so it arrives undefined — coerce to a valid display token (the
      // 30d default for undefined OR any out-of-vocabulary value) rather than
      // leaving the <select> blank.
      frequencyPeriod: coerceFrequentPeriod(config.frequent_complaint_period),
      repeatedThreshold: String(config.repeated_complaint_threshold),
      // AI-302: the auto_resolution_* fields are optional — fall back to the
      // defaults when absent (pre-BE), and map the integer day-count to display.
      autoResolutionThreshold: String(
        config.auto_resolution_threshold ?? DEFAULT_AUTO_RESOLUTION_THRESHOLD
      ),
      autoResolutionPeriod:
        daysToPeriod(config.auto_resolution_period_days) ??
        DEFAULT_DISPLAY_PERIOD,
    }
    // Functional update with a value-equality bailout: returning `prev` lets
    // React skip the re-render entirely. Without this, a query hook that
    // yields a fresh `data` object each render (unstable selector, test mock)
    // would loop forever: effect → setState → re-render → new reference →
    // effect again.
    setState(prev =>
      prev.frequentThreshold === next.frequentThreshold &&
      prev.frequencyPeriod === next.frequencyPeriod &&
      prev.repeatedThreshold === next.repeatedThreshold &&
      prev.autoResolutionThreshold === next.autoResolutionThreshold &&
      prev.autoResolutionPeriod === next.autoResolutionPeriod
        ? prev
        : next
    )
  }, [config])

  const handleChange = useCallback(
    (partial: EscalationRulesChange) => {
      // Local controlled state updates immediately so the inputs stay
      // responsive; nothing persists until the merchant clicks Save / Save and
      // test. Clear any stale success banner so it doesn't linger over
      // freshly-edited values.
      setState(prev => ({ ...prev, ...partial }))
      clearSuccess()
    },
    [clearSuccess]
  )

  if (isLoading) {
    // The shared Spinner renders no role/aria text, so wrap it in a live status
    // region with an i18n accessible name (WCAG 4.1.2 Name, Role, Value).
    return (
      <div role="status" aria-label={t('escalation_rules_loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // A failed FETCH must surface an error/retry message, NOT the misleading
  // "locked" copy below (which would imply the merchant simply hasn't activated
  // yet). Checked before the onboarding gate so a load failure is never masked.
  if (isFetchError || !config) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="alert">
        <Text.BodyText3 color="cayenne50">
          {t('escalation_rules_fetch_error')}
        </Text.BodyText3>
      </div>
    )
  }

  if (!config.onboarding_completed) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="status">
        <Text.BodyText3 color="grey70">
          {t('escalation_rules_locked')}
        </Text.BodyText3>
      </div>
    )
  }

  return (
    <SettingsPaneChrome
      title={tRules('escalation_rules')}
      subtitle={tRules('escalation_rules_desc')}
    >
      {/* R3: guard whole-route navigation (in-app + refresh/close) while dirty. */}
      {unsavedChangesPrompt}
      {isSaveError && (
        // The uikit Notification (baseui Toast, kind=negative) is the codebase
        // standard for inline error banners — it owns the error palette,
        // spacing, and role="alert" semantics, replacing hand-rolled css().
        <Notification kind="negative" size="full">
          {t('escalation_rules_save_error')}
        </Notification>
      )}
      {saveSuccess && !isSaveError && (
        // Positive confirmation reuses the uikit Notification, but overrides the
        // baseui Toast's default assertive role to a POLITE status region (R5)
        // so the save is announced to screen readers without interrupting.
        <Notification
          kind="positive"
          size="full"
          overrides={successNotificationOverrides}
        >
          {t('escalation_rules_save_success')}
        </Notification>
      )}
      {/* AI-903 (Blocker follow-up): EscalationRulesBody renders its three
          sections as bare Fragment children with no inter-section margin of
          its own (see the comment in EscalationRulesBody.tsx) — on the
          wizard/modal path that 24px rhythm comes from ConfigCard's own
          flex-column gap. SettingsPaneChrome does NOT provide that (it only
          spaces its title block from `children` as a whole), so without this
          wrapper the three sections would stack with zero gap here. Reuses
          ConfigCard's own CONFIG_CARD_GAP constant rather than duplicating
          the '24px' literal. */}
      <div
        data-testid="escalation-rules-sections"
        className={`d-flex flex-column ${css({ gap: CONFIG_CARD_GAP })}`}
      >
        <EscalationRulesBody
          frequentThreshold={state.frequentThreshold}
          frequencyPeriod={state.frequencyPeriod}
          repeatedThreshold={state.repeatedThreshold}
          autoResolutionThreshold={
            showGaming ? state.autoResolutionThreshold : undefined
          }
          autoResolutionPeriod={
            showGaming ? state.autoResolutionPeriod : undefined
          }
          onChange={handleChange}
          disabled={!canManage}
        />
      </div>
      {/* AI-64: the Save footer is admin-only — a view-only operator sees the
          rules read-only (disabled card above) with no persist controls.
          AI-1114: only Save ever renders here — escalation rules aren't
          testable in the simulator, so no Save-and-test button. */}
      {canManage && (
        <div className={`d-flex mt-l ${css({ gap: SAVE_FOOTER_GAP })}`}>
          {/* AI-174: Save is REALLY disabled when there's nothing to persist;
              disables in flight. */}
          <Button
            kind="primary"
            onClick={handleSave}
            disabled={!canSave}
            isLoading={isSaving}
          >
            {t('escalation_rules_save')}
          </Button>
        </div>
      )}
    </SettingsPaneChrome>
  )
}

export default EscalationRulesPane
